import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Summary Report Models - Auto-generated from C# DTOs

export interface SummaryReportDto {
    name: string;
    rigName: string;
    wellName: string;
    pipeSize: string;
    jobType: string;
    jobSubType: string;
    measuredDepth?: number;
    totalVerticalDepth?: number;
    mixes: MixReportDto[];
}

export interface MixReportDto {
    name: string;
    density: number;
    equivalentSack: number;
    yield: number;
    totalFluidPercent: number;
    totalFluidVolume: number;
    mixId: number;
    bhst?: number;
    bhct?: number;
    comments: string;
    materials: MixMaterialDto[];
    thickeningTimeReport: ThickeningTimeReportDto;
    freeWaterReport: FreeWaterReportDto;
    mixabilityInfo: MixabilityObjectDto;
    rheologyReports: RheologyReportDto[];
    fluidLossReports: FluidLossReportDto[];
    compStrengthReport: CompStrengthReportDto[];
    hpHtRheologyReports: HphtRheologyReportDto[];
    spacerHPHTRheologyReports: SpacerHphtRheologyReportDto[];
    staticGelStrengthReports: StaticGelStrengthReportDto[];
    crushCompStrengthReports: CrushCompStrengthReportDto[];
    foamStabilityReports: FoamStabilityReportDto[];
    acidSolubilityReports: AcidSolubilityReportDto[];
    apiSedimentationReports: ApiSedimentationReportDto[];
    basicCrushReports: BasicCrushReportDto[];
    expansionReports: ExpansionReportDto[];
    pressurizedMudScaleReports: PressurizedMudScaleReportDto[];
    pycnometerReports: PycnometerReportDto[];
    shearBondReports: ShearBondReportDto[];
    splittingTSReports: SplittingTsReportDto[];
    triaxilConfinedCSReports: TriaxilConfinedCsReportDto[];
    unConfinedCSReports: UnConfinedCsReportDto[];
    spacerSsstReports: SpacerSsstReportDto[];
    spacerCompatibilityReports: SpacerCompatibilityReportDto[];
    spacerQaqcReports: SpacerQaqcReportDto[];
    // Computed properties from backend
    dryMaterials?: MixMaterialDto[];
    liquidMaterials?: MixMaterialDto[];
}

export interface MixMaterialDto {
    materialId: number;
    materialName: string;
    functionName: string;
    volume: number;
    weight: number;
    percent: number;
    materialType: number;
    isSlurryDryMaterial: boolean;
    spacerUom: string;
}

export interface RheologyReportDto {
    temperature: number;
    masterId: number;
    type: RheologyTypes;
    bobAndSleeveType: BobAndSleeveTypes;
    notes?: string;
    results: RheologyResultDto[];
}

export interface RheologyResultDto {
    k1?: number;
    k2?: number;
    measuredRPM600?: number;
    measuredRPM300?: number;
    measuredRPM200?: number;
    measuredRPM100?: number;
    measuredRPM60?: number;
    measuredRPM30?: number;
    measuredRPM20?: number;
    measuredRPM10?: number;
    measuredRPM6?: number;
    measuredRPM3?: number;
    measured10SecGelStrength?: number;
    measured10MinGelStrength?: number;
    pv?: number;
    yp?: number;
    comments?: string;
}

export interface ThickeningTimeReportDto {
    notes?: string;
    initialTemperature: number;
    initialPressure: number;
    finalTemperature: number;
    finalPressure: number;
    pressureRampTime: number;
    temperatureRampTime: number;
    bhst: number;
    targetTimeFrom?: number;
    targetTimeTo?: number;
    gradient?: number;
    conditioningTime?: number;
    isHesitation: boolean;
    stopMotor?: number;
    startMotor?: number;
    isShutdown: boolean;
    results: ThickeningTimeResultDto[];
}

export interface ThickeningTimeResultDto {
    c26Bc?: number;
    c70Bc?: number;
    terminationTime?: number;
    comments?: string;
    lastRecordBy?: string;
    lastRecordDate?: string;
}

export interface FreeWaterReportDto {
    angle: number;
    temperature: number;
    results: FreeWaterResultDto[];
    notes?: string;
}

export interface FreeWaterResultDto {
    freeFluidVolume?: number;
    freeFluidPercent?: number;
    angle?: number;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface MixabilityObjectDto {
    masterId?: number;
    score?: number;
    timeToWetSolids?: number;
}

export interface FluidLossReportDto {
    masterId: number;
    type: FluidLossTypes;
    temperature: number;
    notes?: string;
    results: FluidLossResultDto[];
}

export interface FluidLossResultDto {
    calculatedFluidLoss?: number;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface CompStrengthReportDto {
    masterId: number;
    type: CompStrengthTypes;
    notes?: string;
    results: CompStrengthUcaDto[];
}

export interface CompStrengthUcaDto {
    id: number;
    testOutputMasterId: number;
    compressiveStrengthTypeId?: number;
    pressure?: number;
    psiIn100?: number;
    psiIn500?: number;
    pressure12Hrs?: number;
    pressure24Hrs?: number;
    pressure48Hrs?: number;
    endPSI?: number;
    condTime?: number;
    condTemp?: number;
    testTemp?: number;
    compTestEndTime?: number;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface HphtRheologyReportDto {
    notes?: string;
    masterId: number;
    testType: HpHtRheologyTypes;
    results: HphtRheologyResultDto[];
}

export interface SpacerHphtRheologyReportDto {
    notes?: string;
    masterId: number;
    testType: HpHtRheologyTypes;
    results: HphtRheologyResultDto[];
}

export interface HphtRheologyResultDto {
    readingId: number;
    temperature?: number;
    pressure?: number;
    time?: number;
    comments?: string;
    rampUp?: HphtRheologyReadingDto;
    rampDown?: HphtRheologyReadingDto;
    average?: HphtRheologyReadingDto;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface HphtRheologyReadingDto {
    measuredRPM600?: number;
    measuredRPM300?: number;
    measuredRPM200?: number;
    measuredRPM100?: number;
    measuredRPM60?: number;
    measuredRPM30?: number;
    measuredRPM20?: number;
    measuredRPM10?: number;
    measuredRPM6?: number;
    measuredRPM3?: number;
    measured10sec?: number;
    measured10min?: number;
}

export interface StaticGelStrengthReportDto {
    notes?: string;
    masterId: number;
    jpt: number;
    bottomHydrostaticPressure: number;
    equipmentType: StaticGelStrengthTypes;
    results: StaticGelStrengthResultDto[];
}

export interface StaticGelStrengthResultDto {
    timeTo100Lbs?: number;
    timeTo200Lbs?: number;
    timeTo300Lbs?: number;
    timeTo400Lbs?: number;
    timeTo500Lbs?: number;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface CrushCompStrengthReportDto {
    masterId: number;
    type: CompStrengthTypes;
    notes?: string;
    results: CrushCompStrengthResultDto[];
}

export interface CrushCompStrengthResultDto {
    id: number;
    condTime?: number;
    pressure?: number;
    condTemp?: number;
    testTemp?: number;
    cubeNo1?: number;
    cubeNo2?: number;
    cubeNo3?: number;
    cubeNo4?: number;
    cubeNo5?: number;
    average: number;
    lengthofTest?: number;
    testOutputMasterID: number;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface FoamStabilityReportDto {
    notes?: string;
    masterId: number;
    results: FoamStabilityResultDto[];
}

export interface FoamStabilityResultDto {
    density?: number;
    temperature?: number;
    stable?: boolean;
    comments?: string;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface AcidSolubilityReportDto {
    notes?: string;
    masterId: number;
    results: AcidSolubilityResultDto[];
}

export interface AcidSolubilityResultDto {
    temperature?: number;
    initialMass?: number;
    c1hr?: number;
    c4hr?: number;
    c8hr?: number;
    c12hr?: number;
    c1day?: number;
    c2day?: number;
    c3day?: number;
    c4day?: number;
    c7day?: number;
    c1hrPercent?: number;
    c4hrPercent?: number;
    c8hrPercent?: number;
    c12hrPercent?: number;
    c1dayPercent?: number;
    c2dayPercent?: number;
    c3dayPercent?: number;
    c4dayPercent?: number;
    c7dayPercent?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface ApiSedimentationReportDto {
    notes?: string;
    masterId: number;
    results: ApiSedimentationResultDto[];
}

export interface ApiSedimentationResultDto {
    temperature?: number;
    pressure?: number;
    curingTime?: number;
    piece1?: number;
    piece2?: number;
    piece3?: number;
    piece4?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface BasicCrushReportDto {
    notes?: string;
    masterId: number;
    results: BasicCrushResultDto[];
}

export interface BasicCrushResultDto {
    loadRate?: number;
    csValue?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface ExpansionReportDto {
    notes?: string;
    masterId: number;
    results: ExpansionResultDto[];
}

export interface ExpansionResultDto {
    temperature?: number;
    pressure?: number;
    curingTime?: number;
    percentExpansion?: number;
    percentShrinkable?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface PressurizedMudScaleReportDto {
    notes?: string;
    masterId: number;
    results: PressurizedMudScaleResultDto[];
}

export interface PressurizedMudScaleResultDto {
    density?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface PycnometerReportDto {
    notes?: string;
    masterId: number;
    results: PycnometerResultDto[];
}

export interface PycnometerResultDto {
    sampleID?: string;
    sg?: number;
    aveDensity?: number;
    rVolume?: number;
    cVolume?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface ShearBondReportDto {
    notes?: string;
    masterId: number;
    results: ShearBondResultDto[];
}

export interface ShearBondResultDto {
    temperature?: number;
    pressure?: number;
    curingTime?: number;
    strength?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface SplittingTsReportDto {
    notes?: string;
    masterId: number;
    results: SplittingTsResultDto[];
}

export interface SplittingTsResultDto {
    stsValue1?: number;
    stsValue2?: number;
    stsValue3?: number;
    stsValue4?: number;
    stsValue5?: number;
    stsValue6?: number;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface TriaxilConfinedCsReportDto {
    notes?: string;
    masterId: number;
    results: TriaxilConfinedCsResultDto[];
}

export interface TriaxilConfinedCsResultDto {
    slurryTriaxilConfinedCSOutputReadingID: number;
    loadRate?: number;
    confPress?: number;
    temp?: number;
    youngModulus?: number;
    pr?: number;
    csValue?: number;
    confCSType: number;
    cylinderName: string;
    showCylinderName: boolean;
    slurryTriaxilConfinedCSOutputID: number;
    isRecentOutPut: boolean;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface UnConfinedCsReportDto {
    notes?: string;
    masterId: number;
    results: UnConfinedCsResultDto[];
}

export interface UnConfinedCsResultDto {
    slurryUnConfinedCSOutputReadingID: number;
    loadRate?: number;
    youngModulus?: number;
    pr?: number;
    csValue?: number;
    confCSType: number;
    cylinderName: string;
    showCylinderName: boolean;
    slurryUnConfinedCSOutputID: number;
    isRecentOutPut: boolean;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface SpacerSsstReportDto {
    notes?: string;
    masterId: number;
    temperature?: number;
    conductivityofPureSpacer?: number;
    startingMudVolume?: number;
    comments?: string;
    results: SpacerSsstResultDto[];
}

export interface SpacerSsstResultDto {
    spacerAddedml?: number;
    spacerPercentage?: number;
    conductivity?: number;
    conductivityPercentage?: number;
    baseline?: number;
    isRecentOutPut: boolean;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface SpacerCompatibilityReportDto {
    notes?: string;
    masterId: number;
    temperature?: number;
    results: SpacerCompatibilityResultDto[];
}

export interface SpacerCompatibilityResultDto {
    comments?: string;
    measuredRPM600?: number;
    measuredRPM300?: number;
    measuredRPM200?: number;
    measuredRPM100?: number;
    measuredRPM60?: number;
    measuredRPM30?: number;
    measuredRPM20?: number;
    measuredRPM10?: number;
    measuredRPM6?: number;
    measuredRPM3?: number;
    spacerPercent: number;
    isSlurryBased: boolean;
    fluidMixture: string;
    spacerCompatibilityRatioType1?: number;
    spacerCompatibilityRatioType2?: number;
    isRecentOutPut: boolean;
    lastRecordDate?: string;
    lastRecordBy?: string;
}

export interface SpacerQaqcReportDto {
    notes?: string;
    comments?: string;
    masterId: number;
    results: SpacerQaqcResultDto[];
}

export interface SpacerQaqcResultDto {
    measuredRPM600?: number;
    measuredRPM300?: number;
    measuredRPM200?: number;
    measuredRPM100?: number;
    measuredRPM60?: number;
    measuredRPM30?: number;
    measuredRPM20?: number;
    measuredRPM10?: number;
    measuredRPM6?: number;
    measuredRPM3?: number;
    pv?: number;
    ty?: number;
    title: string;
    titleTemperature?: number;
    titleId?: number;
    isRecentOutPut: boolean;
    lastRecordDate?: string;
    lastRecordBy: string;
}

// Enums
export enum RheologyTypes {
    Surface = 1,
    Downhole = 2
}

export enum BobAndSleeveTypes {
    R1B1 = 1,
    R1B5 = 2,
    Fysa = 3
}

export enum FluidLossTypes {
    Static = 1,
    Stirred = 2
}

export enum HpHtRheologyTypes {
    Fan77 = 1,
    Chann7600 = 2,
    Chann7550 = 3
}

export enum StaticGelStrengthTypes {
    SGSA = 1,
    MACS = 2
}

export enum CompStrengthTypes {
    Uca = 1,
    Crush = 2,
    ConditionedUCA = 3,
    FoamUca = 4,
    FoamCrush = 5,
    FoamConditionedUCA = 6
}


@Injectable({ providedIn: 'root' })
export class SummaryReportService {

    constructor(private http: HttpClient) {
    }
    private readonly baseUrl = environment.apiUrl;

    /**
      * Get summary report for a project
      * @param projectId The ID of the project to get the summary report for
      * @returns Observable of SummaryReportDto containing all slurries and test results
      */
    getSummaryReport(projectId: number): Observable<SummaryReportDto> {
        return this.http.get<SummaryReportDto>(`${this.baseUrl}/project-details/project/${projectId}`);
    }
}
